// app/javascript/controllers/ui/manifesto/marquee_controller.js

import { Controller } from "@hotwired/stimulus"

/**
 * Marquee Controller
 *
 * Бесшовный горизонтальный marquee. Контроллер клонирует внутренний трек
 * столько раз, чтобы покрыть ширину контейнера ×2, потом анимирует translateX.
 *
 * Важно: внутренний `.mf-marquee__track` должен содержать один цикл контента.
 *
 * @usedby _marquee.html.erb, _counter_strip.html.erb
 *
 * Usage:
 *   <div class="mf-marquee"
 *        data-controller="ui--manifesto--marquee"
 *        data-ui--manifesto--marquee-speed-value="60"
 *        data-ui--manifesto--marquee-direction-value="left">
 *     <div class="mf-marquee__track">
 *       <span>MONKEY</span><span>·</span><span>MACHINE</span> ...
 *     </div>
 *   </div>
 *
 * CSS (в manifesto.css): см. .mf-marquee*
 */
export default class extends Controller {
  static targets = ["track"]
  static values = {
    speed: { type: Number, default: 60 },         // px per second
    direction: { type: String, default: "left" }, // left | right
    pauseOnHover: { type: Boolean, default: true }
  }

  connect() {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    this.track = this.hasTrackTarget
      ? this.trackTarget
      : this.element.querySelector(".mf-marquee__track")
    if (!this.track) return

    this.#buildLoop()
    this.#start()

    if (this.pauseOnHoverValue) {
      this.element.addEventListener("mouseenter", this.#pause)
      this.element.addEventListener("mouseleave", this.#resume)
    }

    this.resizeObserver = new ResizeObserver(() => this.#recalc())
    this.resizeObserver.observe(this.element)
  }

  disconnect() {
    if (this.raf) cancelAnimationFrame(this.raf)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    this.element.removeEventListener("mouseenter", this.#pause)
    this.element.removeEventListener("mouseleave", this.#resume)
  }

  #buildLoop() {
    // Clone track content until we have > 2x container width for seamless loop
    const containerW = this.element.offsetWidth
    let trackW = this.track.offsetWidth
    if (trackW === 0) return

    // We need at least 2 full track widths of content for seamless wrap
    const needed = Math.ceil((containerW * 2) / trackW) + 1
    const originalChildren = Array.from(this.track.children).map(c => c.cloneNode(true))

    // We keep original, then append (needed-1) sets
    for (let i = 0; i < needed - 1; i++) {
      for (const child of originalChildren) {
        this.track.appendChild(child.cloneNode(true))
      }
    }

    this.singleLoopWidth = trackW
  }

  #recalc() {
    // On resize: recompute single-loop width (assumes track contains multiple copies of original)
    // Simple heuristic: width of first child-group equals original loop
    // To keep it simple, we reset transform to 0 on resize.
    this.offset = 0
    this.track.style.transform = "translate3d(0, 0, 0)"
  }

  #start() {
    this.offset = 0
    this.lastTs = 0
    this.paused = false
    const dir = this.directionValue === "right" ? 1 : -1

    const step = (ts) => {
      if (!this.lastTs) this.lastTs = ts
      const dt = (ts - this.lastTs) / 1000
      this.lastTs = ts

      if (!this.paused) {
        this.offset += dir * this.speedValue * dt
        // Wrap seamlessly
        const loop = this.singleLoopWidth || 1
        if (this.offset <= -loop) this.offset += loop
        if (this.offset >= loop) this.offset -= loop
        this.track.style.transform = `translate3d(${this.offset}px, 0, 0)`
      }

      this.raf = requestAnimationFrame(step)
    }
    this.raf = requestAnimationFrame(step)
  }

  #pause = () => { this.paused = true }
  #resume = () => { this.paused = false; this.lastTs = 0 }
}
